import interactionResponse from 'await-interaction-response';
import classnames from 'classnames';
import { useEffect, useState, type FunctionComponent } from 'react';
import { useDispatch } from 'react-redux';

import { useBooleanFlags } from '@/domains/core/flags/flags.hooks';
import { isB2B } from '@/domains/core/settings/utils';
import { Button } from '@/domains/core/tamagoshiv2/components/Button/Button';
import { useEligibilityCoupon } from '@/domains/loyalty/modules/CouponEligibility/hooks/useEligibilityCoupon';
import { ABTests } from '@/domains/proB2BServices/AddToWishlist/abTests';
import { AddToWishListButton } from '@/domains/proB2BServices/AddToWishlist/AddToWishListButton';
import { StringAbTests } from '@/domains/productDiscovery/ProductPage/constants';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';
import { useQuantity } from '@/domains/productDiscovery/ProductPage/hooks/useQuantity';
import { useBrand } from '@/domains/productDiscovery/ProductPage/modules/Brand/useBrand';
import { getTagLabels } from '@/domains/productDiscovery/ProductPage/utils/gtm/tagLabels';
import { usePlaylistReconciliation } from '@/domains/productDiscovery/Recommendation/modules/ProductRecommendation/hooks/usePlaylistReconciliation';
import { addToCart as dispatchAddToCart } from '@/domains/shoppingCart/AddToCart/AddToCart.actions';
import type { TrackedProductDetails } from '@/domains/shoppingCart/common/interfaces/TrackedProductDetails';
import { useAddedToCartModal } from '@/domains/shoppingCart/hooks/useAddedToCartModal';
import { getTags } from '@/productDiscovery/CommercialAnimation/utils/getProductPromoTags';
import { useStock } from '@/productDiscovery/ProductPage/hooks/useStock';
import { type XBorderVat } from '@/productDiscovery/ProductPage/hooks/useXBorderVat';

import { addToCart } from './translations';

import styles from './addToCart.module.scss';

interface Props {
  isSticky?: boolean;
  isRedesign?: boolean;
  selectedInsuranceOfferId?: string | null;
  shouldDisablePurchase?: boolean;
  b2bPriceAdvantage?: number;
  vatStatus?: XBorderVat;
}

export const AddToCart: FunctionComponent<Props> = ({
  isSticky = false,
  shouldDisablePurchase = false,
  b2bPriceAdvantage = undefined,
  vatStatus,
}) => {
  const modalState = useAddedToCartModal();
  const [isProductPageRevampEnabled] = useBooleanFlags([
    StringAbTests.ENABLE_PRODUCT_PAGE_REVAMP,
  ]);

  const detail = useDetail<'b2c'>();
  const dispatch = useDispatch();
  const {
    productId,
    title,
    media,
    isSellable,
    price,
    stock,
    seller,
    productRef,
    delivery,
    isSecondHand,
    breadcrumbs,
    averageRating,
    totalRating,
    attributes,
    isOnTopSale,
    modelId,
    articleId,
    categoryId,
    mdScores,
  } = detail;
  const brand = useBrand();

  const { eligibilityCoupon } = useEligibilityCoupon(); // TODO: check with tracking is this is needed.
  const coupons = eligibilityCoupon
    ? [
        {
          code: eligibilityCoupon.code,
          amount: String(eligibilityCoupon.amount),
        },
      ]
    : undefined;
  const [isWishlistRevampEnabled] = useBooleanFlags([
    ABTests.WISHLIST_REVAMP_ENABLED,
  ]);

  const quantity = useQuantity();
  const totalStock = useStock();

  const foreignSeller = vatStatus === 'nonDeductible';

  const {
    playlistFingerprint,
    playlistReconciliation,
    clearPlaylistReconciliation,
  } = usePlaylistReconciliation();

  const hasPromo =
    getTags(
      detail.price?.primaryPrice.amountWithVat ?? 0,
      detail.price?.retailPrice?.amountWithVat ?? 0,
      detail.commercialAnimation?.tags,
    ).length > 0;

  // extracted from Price.tsx
  const hasB2bBenefits = vatStatus !== undefined;

  const priceType = hasB2bBenefits ? 'amountWithoutVat' : 'amountWithVat';

  // Price actually paid by the customer
  const finalPrimaryPrice = price?.primaryPrice[priceType];

  // Price before the potential reduction
  const finalRetailPrice = price?.retailPrice
    ? price?.retailPrice?.[priceType]
    : price?.primaryPrice[priceType];

  // extracted from Claims.tsx
  const breadCrumbs = breadcrumbs || [];

  const lowestCategory = breadcrumbs[breadcrumbs.length - 1];

  const addToCartProduct: TrackedProductDetails = {
    quantity,
    event_trigger: isSticky ? 'Floating CTA' : 'Main CTA',
    id: productId || 0,
    unitPrice: price?.primaryPrice.amountWithVat || 0,
    unitPriceExcVat: price?.primaryPrice.amountWithoutVat || 0,
    b2b: isB2B(),
    brandName: `${brand.name}`,
    brandUrl: `${brand.url}`,
    foreignSeller,
    increment: 1,
    isDeliveredByManomano: delivery?.fulfillment || false,
    minQuantity: stock?.minimumValue || 1,
    name: title || '',
    onSale: false,
    picture: media[0]?.regularUrl || '',
    reference: productRef,
    sample: false,
    sellerName: seller?.name || '',
    stockSize: totalStock || 1,
    oldUnitPrice: price?.retailPrice?.amountWithVat || 0,
    oldUnitPriceExcVat: price?.retailPrice?.amountWithoutVat || 0,
    articleId,
    isSecondHand,
    // Change name when funnel add data to tracking
    pr_lowest_category_name: lowestCategory?.name,
    pr_lowest_category_id: lowestCategory?.id,
    pr_family_name: breadCrumbs[0]?.name,
    pr_family_id: `${breadCrumbs[0]?.id}`,
    pr_category_name: breadCrumbs[1]?.name,
    pr_category_id: `${breadCrumbs[1]?.id || categoryId}`,
    pr_subcategory_name: breadCrumbs[2]?.name,
    pr_subcategory_id: `${breadCrumbs[2]?.id}`,
    pr_subsubcategory_name: breadCrumbs[3]?.name,
    pr_subsubcategory_id: `${breadCrumbs[3]?.id}`,
    pr_price: finalPrimaryPrice,
    pr_rating: averageRating,
    pr_rating_count: totalRating,
    pr_model_name: title,
    pr_model_id: `${modelId}`,
    pr_brand_id: `${brand.id}`,
    pr_attributes_count: attributes?.length,
    pr_base_price: finalRetailPrice,
    pr_is_restocking: false,
    pr_is_top_sales: isOnTopSale,
    pr_seller_id: `${seller?.id}`,
    pr_is_mf: Boolean(delivery?.fulfillment),
    coupons,
    pr_is_in_promo: hasPromo,
    pro_price_advantage_value: b2bPriceAdvantage ?? undefined, // TODO: Remove this call on the parent component
    pr_label: getTagLabels(detail, mdScores),
  };

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (
      modalState?.isVisible ||
      modalState?.showCrossBorderPurchaseBlockedModal
    ) {
      setIsLoading(false);
    }
  }, [modalState?.isVisible, modalState?.showCrossBorderPurchaseBlockedModal]);

  const onOpenAddToCart = async () => {
    if (!isLoading) {
      setIsLoading(true);
      await interactionResponse();
      dispatch(
        dispatchAddToCart({
          products: [addToCartProduct],
          playlistReconciliation: playlistReconciliation
            ? { playlistFingerprint, playlistReconciliation }
            : undefined,
        }),
      );

      clearPlaylistReconciliation();
    }
  };

  const disabled = !isSellable || shouldDisablePurchase;

  // TODO: Remove the wishlist button when revamp is done "productPageRevamp"
  const AddToWishListComponent = shouldDisablePurchase ? null : (
    <AddToWishListButton />
  );

  return (
    <div
      className={classnames(styles.container, {
        [styles.isSticky]: isSticky,
        [styles.isStickyChatRebranding]: isSticky,
      })}
      data-testid="AddToCart"
    >
      <Button
        className={classnames(styles.button, {
          [styles.loadingButton]: isLoading,
          [styles.isFab]: isSticky,
          [styles.wishListLeftBtnAlignment]: isWishlistRevampEnabled,
        })}
        data-testid="paybox-main-cta"
        content={addToCart}
        disabled={disabled}
        loading={isLoading ? { label: '' } : false}
        onClick={!disabled ? onOpenAddToCart : undefined}
        type="primary"
        size="L"
      />

      {/* TODO: Remove when revamp is done. We keep rendering the wishlist here when revamp AB is disabled */}
      {!isProductPageRevampEnabled && AddToWishListComponent}
    </div>
  );
};

AddToCart.displayName = 'AddToCart';
